import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  RadioGroup,
  RadioGroupItem,
  SingleCombobox,
} from '@commenda/kakashi';
import { Icons } from '@commenda/kakashi/src/components/Icons';
import { useEffect, useState } from 'react';
import { useColumnMappingsForUpload } from '../../common/utils/useColumnMappingForUpload';
import { SALES_TAX_PRODUCTS_COLUMN_LIST_FOR_COLUMN_MAPPING } from '../utils';

interface ColumnMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (mapping: Record<string, string>, updateProtectedColumns: boolean) => void;
  csvColumnsFromUpload: string[];
  fileName: string;
  disabled?: boolean;
}

const ProductsColumnMappingModal = ({
  isOpen,
  onClose,
  onUpload,
  csvColumnsFromUpload = [],
  fileName,
  disabled = false,
}: ColumnMappingModalProps) => {
  const [updateProtectedColumns, setUpdateProtectedColumns] = useState(false);

  const { mappings, handleColumnSelect, saveMappings } =
    useColumnMappingsForUpload({
      entity: 'product',
      templateColumns: SALES_TAX_PRODUCTS_COLUMN_LIST_FOR_COLUMN_MAPPING,
      csvColumns: csvColumnsFromUpload,
      resetKey: isOpen,
      autoMap: true,
    });

  const handleUpload = () => {
    const mappingObject = mappings.reduce(
      (acc, mapping) => {
        if (mapping.csvColumn) {
          acc[mapping.tableColumn] = mapping.csvColumn;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    saveMappings(mappingObject);
    onUpload(mappingObject, updateProtectedColumns);
  };

  const isValid = mappings.every(
    (mapping) => !mapping.required || mapping.csvColumn !== null
  );

  useEffect(() => {
    if (!isOpen) {
      setUpdateProtectedColumns(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="bg-white min-w-[650px] flex flex-col max-h-[90vh]"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <DialogHeader>
          <div className="flex flex-col gap-1">
            <h2 className="txt-large-bold text-text-primary">
              Map CSV columns to Indirect Tax products table
            </h2>
            <p className="txt-small-medium text-text-tertiary">
              <span className="txt-small-regular text-text-tertiary">
                File:
              </span>{' '}
              {fileName}
            </p>
          </div>
        </DialogHeader>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-[1.5fr,40px,2fr] gap-6 mb-3">
            <div className="flex items-center gap-3">
              <span className="w-4" />
              <div className="txt-small-medium text-text-tertiary">
                Table Column
              </div>
              <span />
            </div>
            <div />
            <div className="txt-small-medium text-text-tertiary">
              CSV Column
            </div>
          </div>
          {mappings.map((mapping) => (
            <div
              className="grid grid-cols-[1.5fr,40px,2fr] gap-6 items-center mb-3"
              key={mapping.tableColumn}
            >
              <div className="flex items-center gap-2">
                <div className="w-5">
                  {!mapping.csvColumn && (
                    <Icons.AlertCircle
                      className={`size-4 ${mapping.required
                          ? 'text-fg-error-hover'
                          : 'text-fg-quaternary'
                        }`}
                    />
                  )}
                  {mapping.csvColumn && (
                    <Icons.CheckCircle className="size-4 text-fg-success" />
                  )}
                </div>
                <span className="txt-medium-medium text-text-primary">
                  {mapping.tableColumn}
                  {mapping.required && (
                    <span className="ml-0.5 txt-small-medium text-utility-violet-600">
                      *
                    </span>
                  )}
                </span>
              </div>
              <Icons.ArrowRight className="size-5 text-fg-quaternary justify-self-center" />
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <SingleCombobox
                    value={mapping.csvColumn || ''}
                    hideSearch
                    setValue={(value) =>
                      handleColumnSelect(mapping.tableColumn, value)
                    }
                    options={csvColumnsFromUpload
                      .filter(
                        (column) =>
                          column === mapping.csvColumn ||
                          !mappings.some((m) => m.csvColumn === column)
                      )
                      .map((column) => ({
                        value: column,
                        label: column,
                      }))}
                    placeholder={`Select column ${mapping.required ? ' (required)' : ''
                      }`}
                    _className="txt-medium-regular text-text-secondary h-11 px-3"
                    renderTrigger={(val) => (
                      <span className="txt-medium-medium text-text-primary">
                        {val.selectedOption?.label || val.placeholder}
                      </span>
                    )}
                  />
                </div>
                <div
                  className="flex items-center justify-center size-8 hover:bg-bg-secondary rounded-md"
                  onClick={() => handleColumnSelect(mapping.tableColumn, null)}
                >
                  <Icons.XClose className="size-4 text-fg-quaternary" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 pb-6 border-t border-border-primary pt-4">
          <div className="txt-medium-medium mb-3">
            Do you want to allow overriding Product SKU and Tax Code?
          </div>
          <RadioGroup
            value={updateProtectedColumns ? "yes" : "no"}
            onValueChange={(value) => setUpdateProtectedColumns(value === "yes")}
            className="flex flex-col gap-3"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id="yes" />
              <label htmlFor="yes" className="txt-small-regular text-text-primary">
                Yes
              </label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id="no" />
              <label htmlFor="no" className="txt-small-regular text-text-primary">
                No
              </label>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onClose} size="sm">
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!isValid || disabled}
              size="sm"
            >
              Upload
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductsColumnMappingModal;
